import { useEffect, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Anuncios() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [ipbxs, setIpbxs] = useState([]);
  const [dids, setDids] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [ramais, setRamais] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [filas, setFilas] = useState([]);
  const [ivrs, setIvrs] = useState([]);

  useEffect(() => {
    const unscoped = isSuperAdmin ? { skipTenantScope: true } : undefined;
    Promise.all([
      api.get("/ipbxs", unscoped),
      api.get("/dids", unscoped),
      api.get("/ramais", unscoped),
      api.get("/grupos", unscoped),
      api.get("/filas", unscoped),
      api.get("/ivrs", unscoped),
      isSuperAdmin ? api.get("/tenants", { skipTenantScope: true }) : Promise.resolve({ data: [] }),
    ])
      .then(([i, d, r, g, f, v, t]) => {
        setIpbxs(i.data);
        setDids(d.data);
        setRamais(r.data);
        setGrupos(g.data);
        setFilas(f.data);
        setIvrs(v.data);
        setTenants(t.data);
      })
      .catch(() => {
        setIpbxs([]);
        setDids([]);
        setRamais([]);
        setGrupos([]);
        setFilas([]);
        setIvrs([]);
        setTenants([]);
      });
  }, [isSuperAdmin]);

  const ipbxMap = Object.fromEntries(ipbxs.map((row) => [row.id, row.nome]));
  const tenantMap = Object.fromEntries(tenants.map((row) => [row.id, row.nome]));
  const ramalMap = Object.fromEntries(ramais.map((row) => [row.numero, row.nome]));
  const grupoMap = Object.fromEntries(grupos.map((row) => [row.numero, row.nome]));
  const filaMap = Object.fromEntries(filas.map((row) => [row.numero, row.nome]));
  const ivrMap = Object.fromEntries(ivrs.map((row) => [row.numero, row.nome]));
  const belongsToTenant = (row, tenantId) =>
    !tenantId || row.tenant_id === tenantId || row.universal === true;
  const destinationLabel = (row) => {
    if (row.destino_tipo === "ramal" && row.destino_padrao) return `Ramal: ${row.destino_padrao}${ramalMap[row.destino_padrao] ? ` - ${ramalMap[row.destino_padrao]}` : ""}`;
    if (row.destino_tipo === "grupo" && row.destino_padrao) return `Grupo: ${row.destino_padrao}${grupoMap[row.destino_padrao] ? ` - ${grupoMap[row.destino_padrao]}` : ""}`;
    if (row.destino_tipo === "fila" && row.destino_padrao) return `Fila: ${row.destino_padrao}${filaMap[row.destino_padrao] ? ` - ${filaMap[row.destino_padrao]}` : ""}`;
    if (row.destino_tipo === "ivr" && row.destino_padrao) return `URA: ${row.destino_padrao}${ivrMap[row.destino_padrao] ? ` - ${ivrMap[row.destino_padrao]}` : ""}`;
    if (row.destino_tipo === "ipbx" && row.ipbx_id) return `IPBX: ${ipbxMap[row.ipbx_id] || row.ipbx_id}`;
    return "Sem destino";
  };

  return (
    <CrudPage
      title="Anuncios"
      subtitle="Mensagem, desvio para IPBX ou negacao da chamada"
      endpoint="/anuncios"
      testidPrefix="anuncios"
      headers={[...(isSuperAdmin ? ["Cliente"] : []), "Nome", "Mensagem", "Acao", "Destino", "DID", "Status"]}
      renderRow={(r) => (
        <>
          {isSuperAdmin && <td className="font-medium">{tenantMap[r.tenant_id] || r.tenant_id || "-"}</td>}
          <td className="font-medium">{r.nome}</td>
          <td className="max-w-[360px] truncate text-[color:var(--text-secondary)]">{r.mensagem}</td>
          <td>
            {r.acao === "negar" ? (
              <span className="sbc-tag tag-offline">negar</span>
            ) : (
              <span className="sbc-tag tag-online">encaminhar</span>
            )}
          </td>
          <td>{destinationLabel(r)}</td>
          <td className="font-mono text-xs">{r.did_saida || "-"}</td>
          <td>
            {r.enabled ? (
              <span className="sbc-tag tag-online">ativo</span>
            ) : (
              <span className="sbc-tag tag-offline">inativo</span>
            )}
          </td>
        </>
      )}
      defaultValues={{
        tenant_id: user?.tenant_id || "",
        nome: "",
        mensagem: "",
        audio_path: "",
        acao: "encaminhar",
        destino_tipo: "sem_destino",
        destino_padrao: "",
        ipbx_id: "",
        did_saida: "",
        hangup_cause: "CALL_REJECTED",
        enabled: true,
      }}
      fields={[
        ...(isSuperAdmin
          ? [
              {
                key: "tenant_id",
                label: "Cliente do anuncio",
                type: "select",
                full: true,
                options: [
                  { value: "", label: "-- selecione o cliente --" },
                  ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.nome })),
                ],
                onChange: (next) => ({ ...next, ipbx_id: "", did_saida: "" }),
                hint: "O anuncio ficara disponivel somente para o cliente selecionado.",
              },
            ]
          : []),
        { key: "nome", label: "Nome", full: true, placeholder: "Ex: Aviso de gravacao" },
        {
          key: "mensagem",
          label: "Mensagem do anuncio",
          type: "textarea",
          full: true,
          placeholder: "Sua ligacao sera encaminhada para atendimento.",
          hint: "Se audio_path ficar vazio, o dialplan tenta falar este texto via TTS do Voxyra SBC.",
        },
        {
          key: "audio_upload",
          label: "Upload de audio WAV/MP3",
          type: "file",
          full: true,
          accept: ".wav,.wave,.mp3,audio/wav,audio/mpeg",
          uploadEndpoint: "/anuncios/upload-audio",
          targetKey: "audio_path",
          hint: "Ao enviar, o caminho abaixo e preenchido automaticamente.",
        },
        {
          key: "audio_path",
          label: "Arquivo de audio no sistema",
          mono: true,
          full: true,
          placeholder: "/var/lib/sbc-voxyra/anuncios/aviso.wav",
          hint: "Pode preencher manualmente se o arquivo ja existir no servidor.",
        },
        {
          key: "acao",
          label: "Depois do anuncio",
          type: "select",
          options: [
            { value: "encaminhar", label: "Encaminhar para um destino" },
            { value: "negar", label: "Negar/desligar chamada" },
          ],
          onChange: (next, value) =>
            value === "negar"
              ? { ...next, destino_tipo: "sem_destino", destino_padrao: "", ipbx_id: "" }
              : next,
        },
        {
          key: "destino_tipo",
          label: "Inserir o tipo de destino",
          type: "select",
          hidden: (form) => form.acao !== "encaminhar",
          options: [
            { value: "sem_destino", label: "Sem destino" },
            { value: "ramal", label: "Ramal" },
            { value: "grupo", label: "Grupo de comunicacao" },
            { value: "fila", label: "Fila" },
            { value: "ivr", label: "URA / IVR" },
            { value: "ipbx", label: "IPBX externo" },
          ],
          onChange: (next, value) => ({
            ...next,
            destino_tipo: value,
            destino_padrao: value === "ramal" || value === "grupo" || value === "fila" || value === "ivr" ? "" : next.destino_padrao,
            ipbx_id: value === "ipbx" ? next.ipbx_id : "",
          }),
          hint: "Escolha se o anuncio vai encaminhar para ramal, grupo, fila, URA ou IPBX externo.",
        },
        {
          key: "destino_padrao",
          label: "Inserir o cadastro de destino",
          type: "select",
          hidden: (form) => form.acao !== "encaminhar" || form.destino_tipo !== "ramal",
          options: (form) => [
            { value: "", label: "Selecione o ramal" },
            ...ramais
              .filter((ramal) => ramal.enabled !== false && belongsToTenant(ramal, form.tenant_id))
              .map((ramal) => ({ value: ramal.numero, label: `${ramal.numero} - ${ramal.nome || "Sem nome"}` })),
          ],
        },
        {
          key: "destino_padrao",
          label: "Inserir o cadastro de destino",
          type: "select",
          hidden: (form) => form.acao !== "encaminhar" || form.destino_tipo !== "grupo",
          options: (form) => [
            { value: "", label: "Selecione o grupo" },
            ...grupos
              .filter((grupo) => grupo.enabled !== false && belongsToTenant(grupo, form.tenant_id))
              .map((grupo) => ({ value: grupo.numero, label: `${grupo.numero} - ${grupo.nome || "Sem nome"}` })),
          ],
        },
        {
          key: "destino_padrao",
          label: "Inserir o cadastro de destino",
          type: "select",
          hidden: (form) => form.acao !== "encaminhar" || form.destino_tipo !== "fila",
          options: (form) => [
            { value: "", label: "Selecione a fila" },
            ...filas
              .filter((fila) => fila.enabled !== false && belongsToTenant(fila, form.tenant_id))
              .map((fila) => ({ value: fila.numero, label: `${fila.numero} - ${fila.nome || "Sem nome"}` })),
          ],
        },
        {
          key: "destino_padrao",
          label: "Inserir o cadastro de destino",
          type: "select",
          hidden: (form) => form.acao !== "encaminhar" || form.destino_tipo !== "ivr",
          options: (form) => [
            { value: "", label: "Selecione a URA" },
            ...ivrs
              .filter((ivr) => ivr.enabled !== false && belongsToTenant(ivr, form.tenant_id))
              .map((ivr) => ({ value: ivr.numero, label: `${ivr.numero} - ${ivr.nome || "Sem nome"}` })),
          ],
        },
        {
          key: "ipbx_id",
          label: "Inserir o cadastro de destino",
          type: "select",
          hidden: (form) => form.acao !== "encaminhar" || form.destino_tipo !== "ipbx",
          options: (form) => [
            { value: "", label: "Selecione o IPBX" },
            ...ipbxs
              .filter((ipbx) => belongsToTenant(ipbx, form.tenant_id))
              .map((ipbx) => ({ value: ipbx.id, label: ipbx.nome })),
          ],
        },
        {
          key: "did_saida",
          label: "DID enviado ao IPBX",
          type: "select",
          options: (form) => [
            { value: "", label: "Sem DID especifico" },
            ...dids
              .filter((did) => did.enabled !== false && belongsToTenant(did, form.tenant_id))
              .map((did) => ({ value: did.numero, label: did.numero })),
          ],
        },
        { key: "hangup_cause", label: "Motivo ao negar", mono: true, placeholder: "CALL_REJECTED" },
        { key: "enabled", label: "Ativo", type: "toggle" },
      ]}
    />
  );
}
